import { Button, Form, Input, Select } from 'antd';
import { useState } from 'react';
import { HiOutlineArrowLeft, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { uid } from 'uid';

import { InfoTooltip } from './InfoTooltip';
import {
  AddButton,
  BackButton,
  DeleteButton,
  FormBody,
  FormRow,
  NfeCard,
  NfeCardHeader,
  NfeCardTitle,
  SectionLabel,
  StepTitle,
  StepSubtitle,
  SubmitButton,
} from './styles';

const { Option } = Select;

interface StepManualFormProps {
  cnaes: string[];
  isSubmitting: boolean;
  onSubmit: (customData: any[], description: string) => void;
  onBack: () => void;
}

export const StepManualForm = ({ cnaes, isSubmitting, onSubmit, onBack }: StepManualFormProps) => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([{ key: 0 }]);
  const [isDirty, setIsDirty] = useState(false);

  const handleAddRow = () => {
    setDataSource([...dataSource, { key: uid() as any }]);
  };

  const handleRemoveRow = (key: any) => {
    if (dataSource.length > 1) {
      setDataSource(dataSource.filter((item) => item.key !== key));
    }
  };

  const formatCpfCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return (Number(numbers) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleFinish = (values: any) => {
    const customData = dataSource.map((item) => ({
      name: values[`name_${item.key}`],
      doc: values[`doc_${item.key}`],
      cod: values[`cod_${item.key}`],
      description: values[`description_${item.key}`],
      amount: values[`amount_${item.key}`],
    }));

    onSubmit(customData, values.description || '');
  };

  return (
    <FormBody>
      <BackButton
        type="button"
        onClick={() => {
          if (isDirty && !window.confirm('Você tem dados preenchidos. Deseja voltar e perder as alterações?')) {
            return;
          }
          onBack();
        }}
      >
        <HiOutlineArrowLeft />
        Voltar
      </BackButton>

      <StepTitle>Notas Fiscais</StepTitle>
      <StepSubtitle>
        Preencha os dados de cada nota fiscal abaixo. Você pode adicionar mais de uma.
      </StepSubtitle>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        onValuesChange={() => setIsDirty(true)}
      >
        {dataSource.map((record, index) => (
          <NfeCard key={record.key}>
            <NfeCardHeader>
              <NfeCardTitle>Nota {index + 1}</NfeCardTitle>
              {dataSource.length > 1 && (
                <DeleteButton
                  type="button"
                  onClick={() => handleRemoveRow(record.key)}
                  aria-label="Remover nota fiscal"
                >
                  <HiOutlineTrash />
                </DeleteButton>
              )}
            </NfeCardHeader>

            <SectionLabel>Quem vai receber a nota?</SectionLabel>
            <FormRow $columns="1fr 1fr">
              <Form.Item
                name={`name_${record.key}`}
                label={
                  <span>
                    Nome ou Razão social
                    <InfoTooltip text="Nome completo ou razão social de quem vai receber a nota fiscal (o tomador do serviço)." />
                  </span>
                }
                rules={[{ required: true, message: 'Informe o nome' }]}
                style={{ marginBottom: 24 }}
              >
                <Input placeholder="Ex: João da Silva" />
              </Form.Item>
              <Form.Item
                name={`doc_${record.key}`}
                label={
                  <span>
                    CPF ou CNPJ
                    <InfoTooltip text="Documento do tomador. Para pessoa física use o CPF, para empresa use o CNPJ." />
                  </span>
                }
                rules={[{ required: true, message: 'Informe o CPF ou CNPJ' }]}
                style={{ marginBottom: 24 }}
              >
                <Input
                  placeholder="000.000.000-00"
                  maxLength={18}
                  inputMode="numeric"
                  onChange={(e) => {
                    const formatted = formatCpfCnpj(e.target.value);
                    form.setFieldsValue({ [`doc_${record.key}`]: formatted });
                  }}
                />
              </Form.Item>
            </FormRow>

            <SectionLabel>Dados do serviço</SectionLabel>
            <FormRow $columns="1fr 1fr">
              <Form.Item
                name={`cod_${record.key}`}
                label={
                  <span>
                    Código de serviço
                    <InfoTooltip text="Código do serviço prestado (Lista de Serviços da LC 116/2003). Selecione o que melhor se aplica." />
                  </span>
                }
                rules={[{ required: true, message: 'Selecione o código' }]}
                style={{ marginBottom: 20 }}
              >
                <Select placeholder="Toque para selecionar">
                  {cnaes.map((cnae) => (
                    <Option key={cnae} value={cnae}>
                      {cnae}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name={`amount_${record.key}`}
                label={
                  <span>
                    Valor (R$)
                    <InfoTooltip text="Valor total do serviço a ser cobrado nesta nota fiscal, em reais." />
                  </span>
                }
                rules={[{ required: true, message: 'Informe o valor' }]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  placeholder="0,00"
                  maxLength={15}
                  inputMode="numeric"
                  onChange={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    form.setFieldsValue({ [`amount_${record.key}`]: formatted });
                  }}
                />
              </Form.Item>
            </FormRow>
            <Form.Item
              name={`description_${record.key}`}
              label="Descrição do serviço (opcional)"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="Ex: Consultoria em TI" />
            </Form.Item>
          </NfeCard>
        ))}

        <AddButton type="button" onClick={handleAddRow}>
          <HiOutlinePlus />
          Adicionar outra nota fiscal
        </AddButton>

        <Form.Item
          name="description"
          label="Observações para o contador (opcional)"
          style={{ marginTop: 28, marginBottom: 28 }}
        >
          <Input.TextArea
            rows={3}
            placeholder="Se quiser, escreva aqui alguma informação extra sobre esta solicitação..."
          />
        </Form.Item>

        <SubmitButton>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            block
            size="large"
          >
            Enviar Solicitação
          </Button>
        </SubmitButton>
      </Form>
    </FormBody>
  );
};
